import { type FC, useState } from "react";
import { authApi } from "../rpc";

/**
 * ログイン中のメールアドレスに注文が紐づかない (= 一覧が空 / お探しの注文が出ない) ときに、
 * 「ご購入時と異なるメールアドレスかもしれない」ことを気づかせ、別のメールでログインし直す導線を出す。
 *
 * 購入時と違うメールでもマイページには入れてしまうため、注文が表示されず「入れたのに何も出ない」と
 * いう問い合わせが生じる。その気づきを与えるのが目的。
 *
 * - `empty`  : 一覧が空のときの主役表示 (枠付き)。
 * - `subtle` : 一覧に注文があるときも常設する控えめな補足 (複数回購入で一部の注文だけ別メール、の救済)。
 */
type Props = { variant: "empty" | "subtle" };

/** SPA basename は `/mypage` 固定。 */
const LOGIN_PATH = "/mypage/login";

export const TryAnotherEmailNotice: FC<Props> = ({ variant }) => {
  const [submitting, setSubmitting] = useState(false);

  const handleTryAnother = async () => {
    setSubmitting(true);
    try {
      await authApi.logout.$post();
    } catch {
      // ログアウト通信が失敗してもログイン画面へは進める (再ログインでセッションは上書きされる)。
    }
    // セッションを確実にリセットするため、SPA 遷移でなくフルリロードでログイン画面へ移す。
    window.location.assign(LOGIN_PATH);
  };

  const button = (
    <button
      type="button"
      onClick={() => void handleTryAnother()}
      disabled={submitting}
      className="underline underline-offset-4 hover:text-neutral-900 disabled:opacity-50"
    >
      別のメールアドレスでログイン
    </button>
  );

  if (variant === "empty") {
    return (
      <div className="rounded border border-neutral-200 bg-neutral-50 px-5 py-12 text-center text-neutral-600 text-sm">
        <p className="mb-2 font-medium text-neutral-800">ご注文が見つかりませんでした</p>
        <p className="mb-5 text-neutral-500 leading-relaxed">
          ご購入時と異なるメールアドレスでログインされている可能性があります。お心当たりのあるメールアドレスで、改めてログインをお試しください。
        </p>
        {button}
      </div>
    );
  }

  return (
    <p className="mt-6 text-neutral-500 text-xs leading-relaxed">
      お探しのご注文が表示されていない場合は、ご購入時と異なるメールアドレスの可能性があります。{" "}
      {button}
    </p>
  );
};
